/** Date-only arithmetic is deliberately independent of device timezone/DST. */
const DAY = 86400000;
export function dayNumber(value: string | null | undefined): number | null {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  const date = new Date(`${value}T00:00:00Z`);
  return Number.isFinite(date.getTime()) && date.toISOString().slice(0, 10) === value ? date.getTime() / DAY : null;
}
export function addDays(value: string, days: number): string | null {
  const start = dayNumber(value);
  return start === null || !Number.isInteger(days) ? null : new Date((start + days) * DAY).toISOString().slice(0, 10);
}
export function eddFromLmp(lmp: string, cycleDays = 28): string | null {
  return cycleDays > 0 && Number.isInteger(cycleDays) ? addDays(lmp, 280 + cycleDays - 28) : null;
}
export function eddFromArt(transfer: string, embryoAgeDays: number): string | null {
  return Number.isInteger(embryoAgeDays) && embryoAgeDays >= 0 ? addDays(transfer, 266 - embryoAgeDays) : null;
}
export function gaDays(onDate: string, edd?: string | null): number | null {
  const on = dayNumber(onDate),
    due = dayNumber(edd);
  if (on === null || due === null) return null;
  const days = 280 - (due - on);
  return days >= 0 ? days : null;
}
export function formatGa(days: number | null): string {
  return days === null || !Number.isInteger(days) || days < 0 ? '—' : `${Math.floor(days / 7)}w${days % 7}d`;
}
export type BmiLimits = { minHeightCm: number; maxHeightCm: number; minWeightKg: number; maxWeightKg: number };
/** Require configured plausibility limits rather than unchecked height. */
export function bmi(weightKg: number, heightCm: number, limits?: BmiLimits): number | null {
  if (
    !limits ||
    ![limits.minHeightCm, limits.maxHeightCm, limits.minWeightKg, limits.maxWeightKg].every(Number.isFinite) ||
    limits.minHeightCm <= 0 ||
    limits.minWeightKg <= 0 ||
    limits.minHeightCm > limits.maxHeightCm ||
    limits.minWeightKg > limits.maxWeightKg
  )
    return null;
  if (!Number.isFinite(weightKg) || !Number.isFinite(heightCm) || weightKg <= 0 || heightCm <= 0) return null;
  if (
    limits &&
    (heightCm < limits.minHeightCm ||
      heightCm > limits.maxHeightCm ||
      weightKg < limits.minWeightKg ||
      weightKg > limits.maxWeightKg)
  )
    return null;
  return weightKg / (heightCm / 100) ** 2;
}
export function weightGain(
  currentGrams: number | null | undefined,
  baselineGrams: number | null | undefined,
): number | null {
  return currentGrams == null ||
    baselineGrams == null ||
    !Number.isFinite(currentGrams) ||
    !Number.isFinite(baselineGrams) ||
    currentGrams <= 0 ||
    baselineGrams <= 0
    ? null
    : currentGrams - baselineGrams;
}
export function fhDiscrepancy(cm: number, days: number, window: { minDays: number; maxDays: number }): number | null {
  return !Number.isFinite(cm) || cm < 0 || !Number.isFinite(days) || days < window.minDays || days > window.maxDays
    ? null
    : cm - days / 7;
}
export function redatingDifference(
  currentEdd: string,
  proposedEdd: string,
  gaAtScan: number,
  bands: { maxDays: number; differenceDays: number }[],
): { difference: number; exceedsThreshold: boolean } | null {
  const a = dayNumber(currentEdd),
    b = dayNumber(proposedEdd);
  const band = bands.find((item) => gaAtScan >= 0 && gaAtScan <= item.maxDays);
  return a === null || b === null || !band
    ? null
    : { difference: Math.abs(a - b), exceedsThreshold: Math.abs(a - b) > band.differenceDays };
}
export type Outcome = { pregnancyId: string; outcome: 'term' | 'preterm' | 'loss'; livingChildren: number };
/** Never mutate pregnancy history; duplicates/conflicts must be adjudicated before calculation. */
export function gtpal(
  outcomes: Outcome[],
  currentPregnancy: boolean,
): { gravida: number; term: number; preterm: number; abortions: number; living: number } | null {
  if (
    new Set(outcomes.map((o) => o.pregnancyId)).size !== outcomes.length ||
    outcomes.some((o) => !Number.isInteger(o.livingChildren) || o.livingChildren < 0)
  )
    return null;
  return {
    gravida: outcomes.length + Number(currentPregnancy),
    term: outcomes.filter((o) => o.outcome === 'term').length,
    preterm: outcomes.filter((o) => o.outcome === 'preterm').length,
    abortions: outcomes.filter((o) => o.outcome === 'loss').length,
    living: outcomes.reduce((n, o) => n + o.livingChildren, 0),
  };
}
export function weightGainRange(
  bmiValue: number,
  bands: { minBmi: number; maxBmi: number | null; lowLb: number; highLb: number }[],
): [number, number] | null {
  if (!Number.isFinite(bmiValue) || bmiValue <= 0) return null;
  const band = bands.find((b) => bmiValue >= b.minBmi && (b.maxBmi === null || bmiValue < b.maxBmi));
  return band ? [band.lowLb, band.highLb] : null;
}
